import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import React, { useState, useCallback, useEffect } from 'react';
import EditorSettings from '@wix/da-ds/pkg/Icons/24x24/EditorSettings';
import cn from 'classnames';
import { ButtonProps } from '../../../../../components/menus/buttons/types';
import SidePanel from '../../../../../components/menus/SidePanel/SidePanel';
import { useForm, useController } from 'react-hook-form';
import {
  getCurrentUser,
  getExtensionOptions,
  getExternalRenderer,
  getStoredCallback,
} from '../../../../../components/utils/tiptap';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import Flex from '@wix/da-ds/pkg/Flex';
import { DaDeviationAttrs, DaDeviationOptions } from '../../../deviation';
import { useTranslation } from 'react-i18next';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import RefreshIcon from '@wix/da-ds/pkg/Icons/24x24/EditorRefresh';
import ModalHeader from '@wix/da-ds/pkg/modals/dsPresets/Modal/ModalHeader';
import { ExternalCallbacks } from '../../../../../components/Editor/types';
import { PapiDeviation } from '@wix/da-papi-types';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import NumericField from '../../../../../components/lego/NumericField';
import {
  addMissingProtocol,
  validateHref,
} from '../../../../../components/utils/validateHref';
import { TOOLTIP_HOVER_DELAY } from '../../../../../constants';
import EditorIconButton from '../../../../../components/menus/_partials/EditorIconButton';

import buttonStyles from '../../../../../components/menus/buttons/buttons.scss';
import s from './Settings.scss';

interface Inputs {
  width: number;
  widthToggle: boolean;
  link: string;
  alignment: 'left' | 'right' | 'center';
}

const Settings: React.FC<ButtonProps> = ({ editor }) => {
  const { t } = useTranslation('editor');

  const attrs: DaDeviationAttrs = editor.getAttributes(
    'da-deviation'
  ) as DaDeviationAttrs;
  const options = getExtensionOptions<DaDeviationOptions>(
    editor,
    'da-deviation'
  );
  const Preview = getExternalRenderer(editor, 'deviation');
  const [showSettings, setShowSettings] = useState(false);
  const { control, errors, trigger, setValue } = useForm<Inputs>();
  const { field: widthRange } = useController({
    name: 'width',
    control,
    defaultValue: attrs.width ?? options.defaultWidth,
  });

  const { field: widthToggle } = useController({
    name: 'widthToggle',
    control,
    defaultValue: !attrs.width,
  });

  const { field: link } = useController({
    name: 'link',
    control,
    defaultValue: attrs.link ?? '',
    rules: {
      validate: value => {
        return value.length === 0 || validateHref(addMissingProtocol(value));
      },
    },
  });

  const onWidthToggleChange = useCallback(
    e => {
      widthToggle.onChange(e);
      if (e) {
        editor.commands.updateAttributes('da-deviation', { width: null });
        widthRange.onChange(options.defaultWidth);
      }
    },
    [editor.commands, options.defaultWidth, widthRange, widthToggle]
  );

  const onWidthChange = useCallback(
    e => {
      editor.commands.updateAttributes('da-deviation', {
        width: e,
      });
      widthRange.onChange(e);
    },
    [editor.commands, widthRange]
  );

  const onLinkChange = useCallback(
    e => {
      link.onChange(e);
      trigger('link');
      if (e) {
        const href = e.target.value.trim();
        if (href.length === 0 || validateHref(addMissingProtocol(href))) {
          editor.commands.updateAttributes('da-deviation', {
            link: href === '' ? null : addMissingProtocol(href),
          });
        }
      }
    },
    [editor.commands, link, trigger]
  );

  const replaceDeviation = useCallback(() => {
    const onSelectMedia = getStoredCallback<ExternalCallbacks['onSelectMedia']>(
      editor,
      'onSelectMedia'
    );
    onSelectMedia &&
      onSelectMedia((deviations: PapiDeviation[]) => {
        if (deviations.length > 0) {
          editor.chain().focus().daDeviationReplace(deviations[0]).run();
        }
      }, {});
  }, [editor]);

  const refreshDeviation = useCallback(() => {
    editor.chain().focus().daDeviationRefresh().run();
  }, [editor]);

  const closeSettings = () => {
    setShowSettings(false);
  };

  const currentUser = getCurrentUser(editor);
  const allowLink = attrs?.deviation?.author?.userId === currentUser?.userId;

  useEffect(() => {
    setValue('width', attrs.width ?? options.defaultWidth);
    setValue('widthToggle', !attrs.width);
  }, [attrs.width, options.defaultWidth, setValue]);

  return (
    <>
      {showSettings && (
        <SidePanel
          editor={editor}
          onRequestClose={closeSettings}
          header={<ModalHeader heading={t('ext.deviation.settings.title')} />}
          footer={
            <SolidButton
              size="medium"
              variant="approval-green"
              onClick={closeSettings}
            >
              {t('ext.deviation.settings.btn.done')}
            </SolidButton>
          }
        >
          <Flex direction="column" gap={8}>
            <div className={s['preview']}>
              <Preview deviation={attrs.deviation} height={200} width={250} />
              <div className={s['replace-refresh-btn-wrapper']}>
                <IconButton
                  className={cn(s['btn'])}
                  size={'small'}
                  icon={EditIcon}
                  tooltipOffset={[0, 8]}
                  tooltipText={t('ext.deviation.settings.replace')}
                  aria-label={t('ext.deviation.settings.replace')}
                  onClick={replaceDeviation}
                />
                <IconButton
                  className={cn(s['btn'])}
                  size={'small'}
                  icon={RefreshIcon}
                  tooltipOffset={[0, 8]}
                  tooltipText={t('ext.deviation.settings.refresh')}
                  aria-label={t('ext.deviation.settings.refresh')}
                  onClick={refreshDeviation}
                />
              </div>
            </div>
            {options.canResize && (
              <fieldset className={s['size']}>
                <legend>{t('ext.deviation.settings.size.legend')}</legend>
                <BooleanInput
                  id="widthToggle"
                  label={t('ext.deviation.settings.size.range.label')}
                  type="toggle"
                  {...widthToggle}
                  onChange={onWidthToggleChange}
                  noBottomSpacing
                />
                {!widthToggle.value && (
                  <div className={s['width-range']}>
                    <InputLabelText className={s['legend']}>
                      {t('ext.gallery.settings.container')}
                    </InputLabelText>
                    <NumericField
                      label={t('ext.gallery.settings.container.width')}
                      {...widthRange}
                      onChange={onWidthChange}
                      maxValue={options.formMaxWidth ?? options.maxWidth ?? 780}
                      minValue={options.minWidth ?? 10}
                      sliderStep={1}
                    />
                  </div>
                )}
              </fieldset>
            )}
            {options.canLink && allowLink && (
              <TextInput
                id="link"
                label={t('ext.deviation.settings.link.label')}
                placeholder={t('ext.deviation.settings.link.placeholder')}
                {...link}
                onChange={onLinkChange}
                validationResult={errors.link ? 'error' : 'success'}
                details={errors.link ? t('editor.deviation.settings.invalidLink') : undefined}
                onFocus={e => {
                  setTimeout(() => {
                    e.target.scrollIntoView();
                  }, 300);
                }}
              />
            )}
          </Flex>
        </SidePanel>
      )}
      <EditorIconButton
        featureId="node:deviation:settings"
        icon={EditorSettings}
        onClick={() => {
          setShowSettings(true);
        }}
        className={cn(buttonStyles['button'])}
        tooltipHoverDelay={TOOLTIP_HOVER_DELAY}
        tooltipText={t('ext.deviation.toolbar.settings')}
        aria-label={t('ext.deviation.toolbar.settings')}
      />
    </>
  );
};
Settings.displayName = 'Settings';

export default Settings;
